import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingSpinner } from '@src/common/components/LoadingSpinner';
import { useToast } from '@src/common/hooks/useToast';
import { useListConditionsSheets } from '@src/orders/hooks/useListConditionsSheets';
import type { ConditionSheets } from '@src/permits/completePermit/fields/ConditionSheets';
import { type FormConditionSheet } from '@src/permits/completePermit/fields/ConditionSheets';
import { ConditionSheetsField } from '@src/permits/fields/Fields';
import { AddPermitTypeFormInputs } from '@src/permitTypesManager/AddPermitTypeFormInputs';
import { useEditPermitType } from '@src/permitTypesManager/hooks/useEditPermitType';
import type { ExistingPermitTypeDefinition } from '@src/permitTypesManager/PermitTypeDefinition';
import { PermitTypeManagementSheet } from '@src/permitTypesManager/PermitTypeManagementSheet';
import { JurisdictionField } from '@src/permitTypesManager/permitTypeSections/PermitDefinitionFields';
import {
  PermitTypeFormMode,
  permitTypeToFormDefaults,
} from '@src/permitTypesManager/permitTypeToFormDefaults';
import { Navigation } from '@src/routing/Navigation';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export type CustomDocumentFile = { file: File } | { error: boolean } | null;

interface Props {
  permitType: ExistingPermitTypeDefinition;
  customDocumentFile?: CustomDocumentFile;
}

export const EditPermitType = ({ permitType, customDocumentFile }: Props) => {
  const { conditionsSheets, isLoading } = useListConditionsSheets();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <LoadedEditPermitType
      permitType={permitType}
      conditionSheets={conditionsSheets}
      customDocumentFile={customDocumentFile}
    />
  );
};

const LoadedEditPermitType = ({
  permitType,
  conditionSheets,
  customDocumentFile,
}: Props & {
  conditionSheets: ConditionSheets;
  customDocumentFile?: CustomDocumentFile;
}) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();
  const navigate = useNavigate();

  if (
    !!customDocumentFile &&
    'error' in customDocumentFile &&
    customDocumentFile.error
  ) {
    showErrorToast(t('permitTypesManager.edit.error'));
  }

  const defaultValues: AddPermitTypeFormInputs = permitTypeToFormDefaults(
    permitType,
    conditionSheets,
    PermitTypeFormMode.Edit,
    customDocumentFile,
  );

  const form = useForm<AddPermitTypeFormInputs>({
    resolver: yupResolver(AddPermitTypeFormInputs),
    defaultValues,
    mode: 'onSubmit',
  });

  const { editPermitType } = useEditPermitType({
    permitTypeId: permitType.permitTypeId,
    onSuccess: () => {
      showSuccessToast(t('permitTypesManager.edit.success'));
      navigate(
        `/${Navigation.settings.PATH}?${Navigation.settings.searchParamKey}=0`,
      );
    },
    onError: () => {
      showErrorToast(t('permitTypesManager.edit.error'));
    },
    onAlreadyExistsError: () => {
      showErrorToast(t('permitTypesManager.existentPermitError'));
    },
  });

  const handleSubmitAction = () => {
    const values = form.getValues();
    values.conditionsSheets = values.conditionsSheets?.filter(
      (sheet: FormConditionSheet) => sheet.sendToClient,
    );
    editPermitType(values);
  };

  const currentlySelectedJurisdiction = form.watch(JurisdictionField.fieldName);

  useEffect(() => {
    let updatedSheets: FormConditionSheet[] = [];
    if (currentlySelectedJurisdiction === permitType.jurisdiction) {
      updatedSheets = conditionSheets
        .filterForJurisdiction(currentlySelectedJurisdiction)
        .toFormBasedOn(permitType);
    } else {
      updatedSheets = conditionSheets
        .filterForJurisdiction(currentlySelectedJurisdiction)
        .toDefaultForm();
    }
    form.resetField(ConditionSheetsField.fieldName, {
      defaultValue: updatedSheets,
    });
  }, [currentlySelectedJurisdiction, form, conditionSheets, permitType]);
  return (
    <PermitTypeManagementSheet
      form={form}
      pageTitle={t('permitTypesManager.edit.title', {
        permitName: permitType.name,
        interpolation: { escapeValue: false },
      })}
      submitButtonText={t('confirm')}
      onSubmitAction={handleSubmitAction}
      conditionSheets={conditionSheets
        .filterForJurisdiction(currentlySelectedJurisdiction)
        .toFormBasedOn(permitType)}
    />
  );
};
